import { useGetList } from "ra-core";
import { useState } from "react";
import { Loading } from "@/components/admin/loading";

import { SponsoringCreate } from "../sponsorings/SponsoringCreate";
import type { Company } from "../types";
import { DashboardEmpty } from "./DashboardEmpty";
import { DashboardHeader } from "./DashboardHeader";
import { EndingSoonCard, ExpiredCard } from "./ExpiringCards";
import { LeadsBoard } from "./LeadsBoard";
import { SponsoringStats } from "./SponsoringStats";
import { useDashboardFilters } from "./useDashboardFilters";

/**
 * The dashboard, shared by the desktop layout and (through
 * <MobileDashboard>) the phone layout: greeting and search, the six counters,
 * the lead funnel, and the expired / ending-soon cards. New leads are created
 * in a dialog right here, without leaving the page. Every piece is
 * responsive, so the phone shows the same composition with two tiles per row
 * and a horizontally scrolling funnel.
 */
export const Dashboard = () => {
  const { boardStages } = useDashboardFilters();
  const [createStage, setCreateStage] = useState<string | null>(null);
  const { total: totalCompanies, isPending: isPendingCompanies } =
    useGetList<Company>("companies", {
      pagination: { page: 1, perPage: 1 },
    });

  if (isPendingCompanies) {
    // Delayed spinner, so a slow backend does not leave the page blank.
    return <Loading />;
  }

  if (!totalCompanies) {
    return <DashboardEmpty />;
  }

  return (
    <div className="flex flex-col gap-6 md:gap-7">
      <DashboardHeader
        onNewLead={() =>
          setCreateStage(
            // A lead entered by hand is usually one being contacted, not a
            // suspect: prefer "contact" when that stage exists
            boardStages.find((stage) => stage.value === "contact")?.value ??
              boardStages[0]?.value ??
              "contact",
          )
        }
      />
      <SponsoringStats className="md:grid-cols-3 xl:grid-cols-6" />
      <LeadsBoard onAddLead={(stage) => setCreateStage(stage)} />
      <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-2">
        <ExpiredCard />
        <EndingSoonCard />
      </div>
      <SponsoringCreate
        open={createStage != null}
        defaultValues={{ stage: createStage ?? undefined }}
        onClose={() => setCreateStage(null)}
      />
    </div>
  );
};
